import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { SUPPORTED_LOCALES } from "@/i18n";
import { initGoogleTranslate, setGoogleTranslateLanguage } from "@/i18n/googleTranslate";

export default function LanguagePicker({ compact = false }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // If user previously picked a non-English language, re-hydrate Google Translate
  // on every page load so dynamic content stays translated.
  useEffect(() => {
    if (i18n.language && i18n.language !== "en") {
      initGoogleTranslate();
    }
  }, [i18n.language]);

  const current = SUPPORTED_LOCALES.find((l) => l.code === i18n.language) || SUPPORTED_LOCALES[0];

  const change = (code) => {
    if (code === i18n.language) { setOpen(false); return; }
    i18n.changeLanguage(code);
    // Bridge long-form / DB content through Google Translate.
    // This triggers a reload so the UI picks up the new locale cleanly.
    initGoogleTranslate();
    setGoogleTranslateLanguage(code);
  };

  return (
    <div ref={ref} className="relative notranslate" data-testid="language-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("nav.language")}
        data-testid="language-picker-toggle"
        className={
          compact
            ? "inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-forest hover:text-terra transition"
            : "inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-forest hover:text-terra transition px-2 py-1"
        }
      >
        <Globe className="w-4 h-4" strokeWidth={1.8} />
        <span>{current.native}</span>
      </button>

      {open && (
        <div
          role="menu"
          data-testid="language-picker-menu"
          className="absolute right-0 mt-2 w-44 bg-white border border-[#ece4d0] rounded-xl shadow-xl overflow-hidden z-50"
        >
          {SUPPORTED_LOCALES.map((l) => {
            const active = l.code === i18n.language;
            return (
              <button
                key={l.code}
                type="button"
                onClick={() => change(l.code)}
                data-testid={`language-option-${l.code}`}
                className={`w-full text-left flex items-center justify-between gap-3 px-4 py-2.5 text-sm transition ${
                  active ? "bg-[#faf7ec] text-forest" : "text-ink hover:bg-cream-200"
                }`}
              >
                <span className="font-serif">{l.native}</span>
                {active && <Check className="w-4 h-4 text-forest" strokeWidth={2} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
